import { CURRENCIES, plannedHireId, type HireReason } from '@tive/core';
import { useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { Button, Combobox, DateInput, Field, FieldGroup, Input, Modal } from './ui';

/** Create a role that doesn't exist yet, so hiring can be part of a scenario. */
export function HireModal() {
  const managerId = useAppStore((s) => s.hireUnderId);
  const model = useAppStore((s) => s.model);
  const stats = useAppStore((s) => s.stats);
  const squads = useAppStore((s) => s.planSquads);
  const setHireUnder = useAppStore((s) => s.setHireUnder);
  const applyOps = useAppStore((s) => s.applyOps);
  const revealPerson = useAppStore((s) => s.revealPerson);

  const manager = managerId ? model?.byId.get(managerId) : undefined;

  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [department, setDepartment] = useState('');
  const [startDate, setStartDate] = useState('');
  const [location, setLocation] = useState('');
  const [squadId, setSquadId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  /*
   * Which question the role has to answer. Net new by default because that is
   * the one that needs a case made for it — defaulting to backfill would let a
   * new headcount request through with a name in the box and no argument.
   */
  const [kind, setKind] = useState<HireReason['kind']>('net-new');
  const [justification, setJustification] = useState('');
  const [backfillFor, setBackfillFor] = useState('');

  useEffect(() => {
    if (!managerId) return;
    setTitle('');
    setName('');
    setDepartment(manager?.department ?? '');
    setStartDate('');
    // A new role usually sits where its manager does, which is the right guess
    // to offer and the wrong one to force.
    setLocation(manager?.location ?? '');
    setSquadId('');
    setAmount('');
    setCurrency('USD');
    setKind('net-new');
    setJustification('');
    setBackfillFor('');
  }, [managerId, manager?.department, manager?.location]);

  const close = () => setHireUnder(null);

  /**
   * Places already in the org, for the location list.
   *
   * Offered rather than enforced: a role can be opened somewhere the company has
   * nobody yet, which is exactly the interesting case in a reorg.
   */
  const places = [...new Set((model?.people ?? []).flatMap((person) => person.location ?? []))].sort();

  const submit = () => {
    if (!managerId || title.trim().length === 0) return;

    /*
     * The id is decided here rather than by the server.
     *
     * The change is applied optimistically, so something has to know which card
     * to reveal — and letting the server invent its own id means the card just
     * drawn is replaced by a stranger a moment later. `resolveOp` records
     * whatever id the operation carried, so replay lands on this one too.
     */
    const id = plannedHireId();
    const budget = Number(amount.replace(/[^0-9.]/g, ''));

    /*
     * Only the field the chosen kind uses. Core trims and drops the other one
     * anyway, but sending both would mean the operation recorded in the log
     * claimed something nobody typed on purpose.
     */
    const reason: HireReason =
      kind === 'backfill'
        ? { kind: 'backfill', backfillFor: backfillFor.trim() }
        : { kind: 'net-new', justification: justification.trim() };

    /*
     * Closed before the save, not after it. `applyOps` waits for the round trip
     * — which hosted includes writing the file to Drive — and a dialog that sits
     * there for most of a second after the button is pressed reads as a freeze.
     * The optimistic update has already put the card on the chart by now.
     */
    close();

    /*
     * The optimistic half of `applyOps` runs before it awaits anything, so the
     * card is on the chart by the next line — which is why the reveal comes
     * after the call rather than before it. Nothing here waits for the save.
     */
    const saving = applyOps([
      {
        op: 'addHire',
        hire: {
          id,
          managerId,
          title: title.trim(),
          ...(name.trim() ? { name: name.trim() } : {}),
          department: department.trim() || null,
          startDate: startDate.trim() || null,
          location: location.trim() || null,
          ...(budget > 0 ? { cost: { amount: budget, currency } } : {}),
          reason,
        },
      },
      /*
       * A second operation rather than a `teams` value on the hire, because they
       * are not the same thing. `person.teams` is the raw HRIS column, and a
       * squad id put in there names a team that does not exist — which is what
       * the first version did, quietly, so the role arrived with no team at all.
       * Membership of a curated team is a change to the team, and this is the
       * operation that records it.
       *
       * In the same batch: the ops endpoint writes nothing unless every
       * operation succeeds, so the role and its team arrive together or not at
       * all.
       */
      ...(squadId ? [{ op: 'setSquadMember' as const, squadId, personId: id, role: 'member' as const }] : []),
    ]);
    revealPerson(id);
    void saving;
  };

  return (
    <Modal
      open={managerId !== null}
      onClose={close}
      title="Add a planned role"
      description={manager ? `Reporting to ${manager.name} — ${manager.title}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" disabled={title.trim().length === 0} onClick={submit}>
            Add role
          </Button>
        </>
      }
    >
      <Field label="Title" hint="Required.">
        <Input
          autoFocus
          value={title}
          placeholder="e.g. Senior Platform Engineer"
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
      </Field>

      {/*
        Why the role exists, and the toggle comes first because it decides what
        the box below it is asking for. Net new headcount has to be argued for;
        a backfill is argued for by naming who left.
      */}
      <FieldGroup label="Why this role">
        <div className="flex gap-1.5">
          {(['net-new', 'backfill'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={kind === option}
              onClick={() => setKind(option)}
              className={cn(
                'h-9 flex-1 rounded-lg border text-[13px] font-medium transition-colors',
                kind === option
                  ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                  : 'border-[var(--color-line)] text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)]',
              )}
            >
              {option === 'net-new' ? 'Net new' : 'Backfill'}
            </button>
          ))}
        </div>
      </FieldGroup>

      {kind === 'net-new' ? (
        <Field label="Justification" hint="Why the headcount is needed.">
          <Input
            aria-label="Justification"
            value={justification}
            placeholder="e.g. Platform work has no owner"
            onChange={(event) => setJustification(event.target.value)}
          />
        </Field>
      ) : (
        <Field label="Backfilling" hint="Who is being replaced.">
          <Input
            aria-label="Backfilling"
            value={backfillFor}
            placeholder="e.g. Sam Reed"
            onChange={(event) => setBackfillFor(event.target.value)}
          />
        </Field>
      )}

      <Field label="Name" hint="Leave blank for an open req.">
        <Input
          value={name}
          placeholder={title.trim() ? `Open req — ${title.trim()}` : 'Open req'}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FieldGroup label="Department">
          <Combobox
            label="Department"
            value={department}
            onChange={setDepartment}
            options={(stats?.departments ?? []).map((d) => d.department)}
          />
        </FieldGroup>
        <Field label="Target start">
          <DateInput value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FieldGroup label="Location" hint="Where the role sits. Defaults to the manager's.">
          <Combobox
            label="Location"
            value={location}
            onChange={setLocation}
            options={places}
            placeholder="e.g. Remote · Kosovo"
          />
        </FieldGroup>
        <Field label="Team" hint="Delivery team, not the reporting line.">
          <select
            value={squadId}
            onChange={(event) => setSquadId(event.target.value)}
            className="h-9 w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2.5 text-[13px] text-[var(--color-ink)]"
          >
            <option value="">No team</option>
            {squads.squads.map((squad) => (
              <option key={squad.id} value={squad.id}>
                {squad.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/*
        Approximate on purpose, and labelled as such. A headcount plan is made
        weeks before anybody is hired, so a precise figure would be a guess with
        a decimal point on it — what the number is for is the size of the plan.
      */}
      <Field label="Approximate salary" hint="Budget for the role. Optional.">
        <div className="flex gap-2">
          <Input
            value={amount}
            inputMode="numeric"
            placeholder="e.g. 120000"
            onChange={(event) => setAmount(event.target.value)}
          />
          <select
            aria-label="Currency"
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
            className="h-9 shrink-0 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2 text-[13px] text-[var(--color-ink)]"
          >
            {CURRENCIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      </Field>
    </Modal>
  );
}
